import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Bold, Check, X, Loader2, Pencil, Type, Replace } from 'lucide-react';
import DOMPurify from 'dompurify';
import { editingStore } from './EditableText';

const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'br', 'p', 'div', 'span', 'ul', 'ol', 'li'];
const ALLOWED_ATTR = ['style'];
const ALLOWED_STYLES_RE = /^(font-size|font-weight)\s*:\s*[^;]+;?\s*$/i;

/** Safely sanitise HTML the admin has typed, allowing only bold/font-size etc. */
export const sanitizeRichText = (html) => {
  if (!html) return '';
  // Reject style values other than font-size / font-weight
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS, ALLOWED_ATTR,
    ADD_TAGS: [], ADD_ATTR: [],
  });
  // Post-process: strip style attrs that don't match ALLOWED_STYLES_RE
  const wrapper = document.createElement('div');
  wrapper.innerHTML = clean;
  wrapper.querySelectorAll('[style]').forEach((el) => {
    const raw = el.getAttribute('style') || '';
    const kept = raw.split(';').map(s => s.trim()).filter(Boolean).filter(rule => ALLOWED_STYLES_RE.test(rule + ';')).join('; ');
    if (kept) el.setAttribute('style', kept); else el.removeAttribute('style');
  });
  return wrapper.innerHTML;
};

/** Convert plain text (with \n) into safe paragraph-broken HTML for legacy content. */
export const plainToHtml = (text) => {
  if (!text) return '';
  return text
    .split(/\n{2,}/)
    .map(p => `<p>${p.replace(/\n/g, '<br/>').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/&lt;br\/&gt;/g, '<br/>')}</p>`)
    .join('');
};

/** Detects whether a stored description contains HTML markup already. */
const looksLikeHtml = (s) => /<\/?(?:p|br|b|strong|i|em|u|span|div|ul|ol|li)\b/i.test(s || '');

/** Render helper: sanitized description HTML (auto-migrates plain text with paragraph breaks). */
export const renderDescriptionHtml = (raw) => {
  if (!raw) return { __html: '' };
  const html = looksLikeHtml(raw) ? raw : plainToHtml(raw);
  return { __html: sanitizeRichText(html) };
};

const FONT_SIZES = [
  { label: 'Small', value: '0.875rem' },
  { label: 'Normal', value: '1rem' },
  { label: 'Large', value: '1.125rem' },
  { label: 'X-Large', value: '1.25rem' },
  { label: '2X-Large', value: '1.5rem' },
];

/**
 * Rich text field for the admin. Provides Bold + Font-size toolbar and a
 * large (~20-line) editing area. Participates in the same single-active-editor
 * lock as EditableText.
 */
const RichTextField = ({ value, onSave, rows = 20 }) => {
  const [myId] = useState(() => editingStore.makeId());
  const activeId = useSyncExternalStore(editingStore.subscribe, editingStore.getSnapshot);
  const editing = activeId === myId;
  const locked = activeId !== null && !editing;

  const editorRef = useRef(null);
  const [saving, setSaving] = useState(false);

  // Preserve initial HTML for cancel
  const initialHtmlRef = useRef('');

  useEffect(() => () => editingStore.release(myId), [myId]);

  useEffect(() => {
    if (editing && editorRef.current) {
      const startHtml = looksLikeHtml(value) ? value : plainToHtml(value || '');
      editorRef.current.innerHTML = startHtml;
      initialHtmlRef.current = startHtml;
      // Focus at end
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editing, value]);

  const startEdit = () => {
    if (locked) return;
    editingStore.acquire(myId);
  };

  const cancel = () => {
    editingStore.release(myId);
  };

  const save = async () => {
    if (!editorRef.current) return;
    const raw = editorRef.current.innerHTML;
    const cleaned = sanitizeRichText(raw);
    setSaving(true);
    try {
      await onSave(cleaned);
      editingStore.release(myId);
    } catch (e) { /* toast handled upstream */ }
    finally { setSaving(false); }
  };

  const exec = (cmd, arg) => {
    // execCommand is deprecated but still supported everywhere and is the
    // simplest way to run bold/fontsize inside a contenteditable without
    // pulling in a full RTE library.
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
  };

  const applyFontSize = (size) => {
    // Wrap current selection in <span style="font-size: X">
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement('span');
    span.style.fontSize = size;
    try {
      range.surroundContents(span);
    } catch {
      // Fallback: extract & wrap when selection crosses nodes
      const frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);
    }
    sel.removeAllRanges();
    editorRef.current?.focus();
  };

  // --- Find & Replace ---
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [replaceValue, setReplaceValue] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [replaceStats, setReplaceStats] = useState(null);

  const escapeReg = (s) => (s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  /** Walk text nodes only and swap matches — keeps all inline formatting intact. */
  const replaceInEditor = ({ replaceAll }) => {
    setReplaceStats(null);
    if (!editorRef.current || !findQuery) return;
    const flags = caseSensitive ? 'g' : 'gi';
    const re = new RegExp(escapeReg(findQuery), flags);
    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, null);
    const nodes = [];
    let n = walker.nextNode();
    while (n) { nodes.push(n); n = walker.nextNode(); }
    let hits = 0;
    let stoppedAtOne = false;
    for (const node of nodes) {
      const original = node.nodeValue || '';
      if (!re.test(original)) { re.lastIndex = 0; continue; }
      re.lastIndex = 0;
      if (replaceAll) {
        const count = (original.match(re) || []).length;
        hits += count;
        node.nodeValue = original.replace(re, replaceValue);
      } else {
        // Only first hit across the whole editor
        const idx = original.search(re);
        if (idx >= 0) {
          const match = original.match(re);
          const matched = match ? match[0] : findQuery;
          node.nodeValue = original.slice(0, idx) + replaceValue + original.slice(idx + matched.length);
          hits += 1;
          stoppedAtOne = true;
          break;
        }
      }
      re.lastIndex = 0;
      if (stoppedAtOne) break;
    }
    setReplaceStats({ hits, replaceAll });
    editorRef.current?.focus();
  };

  const closeFind = () => {
    setFindOpen(false);
    setFindQuery('');
    setReplaceValue('');
    setReplaceStats(null);
  };

  return (
    <div className={`${editing ? 'ring-2 ring-[#F46A25] rounded-sm bg-white' : ''}`}>
      {!editing ? (
        <button
          type="button"
          onClick={startEdit}
          disabled={locked}
          data-testid="rich-text-trigger"
          className={`group w-full text-left rounded px-2 py-2 border border-neutral-200 hover:border-[#F46A25] hover:bg-orange-50 transition-colors ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          {value ? (
            <div className="text-xs text-neutral-700 prose-sm max-w-none overflow-hidden max-h-24" dangerouslySetInnerHTML={renderDescriptionHtml(value)} />
          ) : (
            <span className="text-xs text-neutral-400 italic">Click to write description…</span>
          )}
          {!locked && (
            <span className="text-[10px] uppercase text-[#F46A25] font-bold mt-1 inline-flex items-center gap-1">
              <Pencil className="w-3 h-3" /> Click to edit
            </span>
          )}
        </button>
      ) : (
        <div className="p-2" data-testid="rich-text-editor">
          <div className="flex items-center gap-1 mb-2 border-b border-neutral-200 pb-2 flex-wrap">
            <button
              type="button"
              onClick={() => exec('bold')}
              data-testid="rich-text-bold"
              className="p-1.5 hover:bg-neutral-100 rounded text-[#12263A] font-bold"
              title="Bold (Ctrl+B)"
            >
              <Bold className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1 ml-2">
              <Type className="w-4 h-4 text-neutral-500" />
              <select
                onChange={(e) => { if (e.target.value) { applyFontSize(e.target.value); e.target.value = ''; } }}
                defaultValue=""
                data-testid="rich-text-fontsize"
                className="text-xs border border-neutral-300 rounded-sm px-2 py-1 focus:outline-none focus:border-[#F46A25]"
              >
                <option value="" disabled>Font size</option>
                {FONT_SIZES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setFindOpen(v => !v)}
              data-testid="rich-text-find-toggle"
              className={`ml-2 text-xs font-bold px-2 py-1 rounded flex items-center gap-1 border ${findOpen ? 'bg-[#F46A25] text-white border-[#F46A25]' : 'bg-white text-[#12263A] border-neutral-300 hover:border-[#F46A25]'}`}
              title="Find & replace text"
            >
              <Replace className="w-3.5 h-3.5" /> Find & Replace
            </button>
            <span className="text-[10px] text-neutral-400 ml-auto">Select text first, then choose size</span>
          </div>
          {findOpen && (
            <div className="mb-2 p-3 border border-orange-200 bg-orange-50/60 rounded-sm" data-testid="rich-text-find-panel">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                <input
                  autoFocus
                  value={findQuery}
                  onChange={e => setFindQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); replaceInEditor({ replaceAll: e.shiftKey ? false : true }); }
                    if (e.key === 'Escape') { e.preventDefault(); closeFind(); }
                  }}
                  placeholder="Find text…"
                  data-testid="rich-text-find-input"
                  className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
                />
                <input
                  value={replaceValue}
                  onChange={e => setReplaceValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); replaceInEditor({ replaceAll: true }); }
                    if (e.key === 'Escape') { e.preventDefault(); closeFind(); }
                  }}
                  placeholder="Replace with…"
                  data-testid="rich-text-replace-input"
                  className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-1 text-xs text-neutral-700">
                  <input
                    type="checkbox"
                    checked={caseSensitive}
                    onChange={e => setCaseSensitive(e.target.checked)}
                    data-testid="rich-text-case-sensitive"
                  />
                  Case-sensitive
                </label>
                <button
                  type="button"
                  onClick={() => replaceInEditor({ replaceAll: false })}
                  disabled={!findQuery}
                  data-testid="rich-text-replace-one"
                  className="text-xs bg-white border border-neutral-300 hover:border-[#F46A25] font-bold px-3 py-1.5 rounded-sm disabled:opacity-50"
                >Replace next</button>
                <button
                  type="button"
                  onClick={() => replaceInEditor({ replaceAll: true })}
                  disabled={!findQuery}
                  data-testid="rich-text-replace-all"
                  className="text-xs bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-3 py-1.5 rounded-sm disabled:opacity-50"
                >Replace all</button>
                <button
                  type="button"
                  onClick={closeFind}
                  className="text-xs text-neutral-500 hover:text-[#12263A] px-2 py-1.5"
                  data-testid="rich-text-find-close"
                >Close</button>
                {replaceStats && (
                  <span className={`ml-auto text-xs font-bold ${replaceStats.hits > 0 ? 'text-green-700' : 'text-neutral-500'}`} data-testid="rich-text-replace-stats">
                    {replaceStats.hits > 0
                      ? `Replaced ${replaceStats.hits} ${replaceStats.hits === 1 ? 'occurrence' : 'occurrences'}`
                      : 'No matches found'}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-neutral-500 mt-2">
                Tip: press <kbd className="px-1 border rounded">Enter</kbd> to Replace all, <kbd className="px-1 border rounded">Esc</kbd> to close. Only text is replaced — formatting stays.
              </div>
            </div>
          )}
          <div
            ref={editorRef}
            contentEditable
            data-testid="rich-text-content"
            suppressContentEditableWarning
            style={{ minHeight: `${rows * 1.5}em`, maxHeight: '60vh' }}
            className="w-full border border-neutral-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25] whitespace-pre-wrap overflow-y-auto"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={cancel}
              className="text-xs border border-neutral-300 hover:border-red-500 hover:text-red-600 font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1"
              data-testid="rich-text-cancel"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-xs bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
              data-testid="rich-text-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextField;
